import { useState, useEffect, useCallback } from 'react';



const CACHE_KEY = 'subscription_plans_cache';
const CACHE_DURATION_MS = 60 * 60 * 1000; // 1 hour
const EXTENDED_CACHE_DURATION_MS = 24 * 60 * 60 * 1000; // 24 hours for fallback

/**
 * Get the API base URL based on environment
 */
function getApiBase() {
  if (typeof window !== 'undefined' && 
      (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')) {
    return '/s1/v1';
  }
  return 'https://api.communitynord.com/s1/v1';
}

/**
 * Get cached plans from localStorage
 */
function getCachedPlans() {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (!cached) return null;
    
    const { plans, timestamp } = JSON.parse(cached);
    const now = Date.now();
    const age = now - timestamp;
    
    // Return cached plans if within normal cache duration
    if (age < CACHE_DURATION_MS) {
      return { plans, isStale: false };
    }
    
    // Return cached plans as stale if within extended duration (for fallback when API is down)
    if (age < EXTENDED_CACHE_DURATION_MS) {
      return { plans, isStale: true };
    }
    
    // Cache is too old, remove it
    localStorage.removeItem(CACHE_KEY);
    return null;
  } catch {
    return null;
  }
}

/**
 * Save plans to localStorage cache
 */
function setCachedPlans(plans) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({
      plans,
      timestamp: Date.now()
    }));
  } catch {
    // localStorage might be full or disabled
  }
}

/**
 * Transform API response to internal format
 * Handles camelCase conversion from PascalCase API response
 * Filters to only include allowed plans
 */
function transformApiResponse(apiPlans) {
  return apiPlans
    .map(plan => ({
      planKey: plan.planKey || plan.PlanKey,
      name: plan.name || plan.Name,
      description: plan.description || plan.Description,
      monthlyPrice: plan.monthlyPrice ?? plan.MonthlyPrice ?? 0,
      currency: plan.currency || plan.Currency || 'NOK',
      isBillable: plan.isBillable ?? plan.IsBillable ?? false,
      limits: transformLimits(plan.limits || plan.Limits)
    }))
    .sort((a, b) => {
      // Sort by price ascending
      return a.monthlyPrice - b.monthlyPrice;
    });
}

/**
 * Transform limits object from API response
 */
function transformLimits(limits) {
  if (!limits) return null;
  
  return {
    maxMembers: limits.maxMembers ?? limits.MaxMembers,
    maxMembershipTiers: limits.maxMembershipTiers ?? limits.MaxMembershipTiers,
    maxBookingsPerMonth: limits.maxBookingsPerMonth ?? limits.MaxBookingsPerMonth,
    maxLocations: limits.maxLocations ?? limits.MaxLocations,
    maxSubLocationsPerLocation: limits.maxSubLocationsPerLocation ?? limits.MaxSubLocationsPerLocation,
    maxBookingOptions: limits.maxBookingOptions ?? limits.MaxBookingOptions,
    maxTicketsPerMonth: limits.maxTicketsPerMonth ?? limits.MaxTicketsPerMonth,
    maxTicketTemplates: limits.maxTicketTemplates ?? limits.MaxTicketTemplates,
    maxVenueLayouts: limits.maxVenueLayouts ?? limits.MaxVenueLayouts,
    maxAdminUsers: limits.maxAdminUsers ?? limits.MaxAdminUsers,
    maxUsers: limits.maxUsers ?? limits.MaxUsers,
    maxStorageMb: limits.maxStorageMb ?? limits.MaxStorageMb,
    maxContentPages: limits.maxContentPages ?? limits.MaxContentPages,
    hasAdvancedBooking: limits.hasAdvancedBooking ?? limits.HasAdvancedBooking ?? false,
    hasCustomBranding: limits.hasCustomBranding ?? limits.HasCustomBranding ?? false,
    hasApiAccess: limits.hasApiAccess ?? limits.HasApiAccess ?? false,
    hasExportAccess: limits.hasExportAccess ?? limits.HasExportAccess ?? false,
    hasPrioritySupport: limits.hasPrioritySupport ?? limits.HasPrioritySupport ?? false,
    hasWhiteLabel: limits.hasWhiteLabel ?? limits.HasWhiteLabel ?? false
  };
}

/**
 * Hook for fetching subscription plans with caching
 * 
 * Features:
 * - 1 hour cache lifetime for normal operation
 * - Extended cache (24h) used as fallback when API is unreachable
 * - Default fallback plans if no cache exists
 * - Automatic background refresh of stale cache
 * - Includes plan limits from API
 */
export function useSubscriptionPlans() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isFromCache, setIsFromCache] = useState(false);

  const fetchPlans = useCallback(async (forceRefresh = false) => {
    // Check cache first (unless force refresh)
    if (!forceRefresh) {
      const cached = getCachedPlans();
      if (cached && !cached.isStale) {
        setPlans(cached.plans);
        setLoading(false);
        setIsFromCache(true);
        return;
      }
      
      // If we have stale cache, use it while fetching fresh data
      if (cached?.plans) {
        setPlans(cached.plans);
        setIsFromCache(true);
        // Don't set loading to true - we have data to show
      }
    }

    try {
      const response = await fetch(`${getApiBase()}/tenants/onboarding/subscription-plans`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`API returned ${response.status}`);
      }

      const apiPlans = await response.json();
      const transformedPlans = transformApiResponse(apiPlans);
      
      setPlans(transformedPlans);
      setCachedPlans(transformedPlans);
      setError(null);
      setIsFromCache(false);
    } catch (err) {
      console.error('Failed to fetch subscription plans:', err);
      
      // If we have cached plans (even stale), keep using them
      const cached = getCachedPlans();
      if (cached?.plans) {
        setPlans(cached.plans);
        setIsFromCache(true);
        setError({ 
          message: 'Using cached data - API unavailable', 
          originalError: err.message 
        });
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  return {
    plans,
    loading,
    error,
    isFromCache,
    refresh: () => fetchPlans(true)
  };
}

export default useSubscriptionPlans;